import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Client, CloudScheduleParameters } from '@app/models/client.model';
import { StorageBattery } from '@app/models/connected-device.model';
import { IrradianceForecast } from '@app/models/irradiance-forecast.model';
import { ConnectedDeviceEnum } from '@web/ui-mobile';
import { WorkingMode } from 'libs/ui-mobile/src/lib/models/enums/working-mode.enum';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { ClientService } from './client.service';
import { DevicesService } from './devices.service';
import { SignalsSimpleStoreService } from './signals/signals-simple-store.service';

export type StoreyWithoutMeterState = {
    origin: 'overview' | 'history' | 'battery-detail' | 'storage-batteries-management' | null;
    batteryId?: string;
};

@Injectable({
    providedIn: 'root',
})
export class StoreyWithoutMeterService extends SignalsSimpleStoreService<StoreyWithoutMeterState> {
    private readonly rootUrl = `${environment.urlBackend}/api/irradiance-forecast`;
    private readonly http = inject(HttpClient);
    private dataSubscription?: Subscription;

    isFetching = signal(false);

    client = signal<Client | undefined>(undefined);
    storey = signal<StorageBattery[] | undefined>(undefined);

    hasCloudScheduleStorey = computed(() => {
        return (
            this.client()?.cloudScheduleParameter !== CloudScheduleParameters.MANUAL &&
            !this.storey()?.every(
                device => device.workingMode === WorkingMode.HUB_REMOTE || device.workingMode === WorkingMode.REMOTE,
            )
        );
    });

    constructor(
        private clientService: ClientService,
        private devicesService: DevicesService,
    ) {
        super();

        this.setState({
            origin: null,
            batteryId: undefined,
        });
    }

    hasGridStreamMeter(): boolean {
        return this.client()?.hasGridStreamMeter || false;
    }

    hasAddress(): boolean {
        return !!this.client()?.adresse;
    }

    hasBaseConsumptionLevel(): boolean {
        return !!this.client()?.baseConsumptionLevel;
    }

    hasInstallPower(): boolean {
        const installPower = this.client()?.installPower;
        if (installPower === null || installPower === undefined) {
            return false;
        }

        return installPower >= 0;
    }

    public getToday(): Observable<IrradianceForecast> {
        return this.http.get<IrradianceForecast>(`${this.rootUrl}`, {
            params: {
                dayRequested: 'TODAY',
            },
        });
    }

    public getTomorrow(threshold?: number): Observable<IrradianceForecast> {
        let params: { dayRequested: string; threshold?: number } = {
            dayRequested: 'TOMORROW',
        };

        if (threshold !== undefined && threshold >= 0) {
            params.threshold = threshold;
        }
        return this.http.get<IrradianceForecast>(`${this.rootUrl}`, {
            params,
        });
    }

    public fetchData(): void {
        this.dataSubscription?.unsubscribe();

        this.dataSubscription = combineLatest([
            this.clientService.get(),
            this.devicesService.getStationsAndStorage(),
        ]).subscribe(([client, devices]) => {
            this.client.set(client);
            this.storey.set(devices.filter(device => device.type === ConnectedDeviceEnum.STOREY));
        });
    }
}
